import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest, getQueryFn, invalidateSiteAuditQueries } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { userHasPermission } from "@/lib/permissions";
import { apiErrorMessage, isSiteAuditInProgress } from "@/lib/site-audit";
import { formatFeedStatusLine, type FeedStatusView } from "@shared/site-audit-feed";
import { Plus, ExternalLink, Trash2, RefreshCw } from "lucide-react";

type AuditStatus =
  | "queued"
  | "collecting_dataforseo"
  | "collecting_pagespeed"
  | "collecting_moz"
  | "analyzing"
  | "complete"
  | "failed";

interface SiteAuditJob {
  id: string;
  domain: string;
  status: AuditStatus;
  createdAt: string;
  updatedAt: string;
  competitors: string[] | null;
  brief: string | null;
  runCount: number;
}

const DELETABLE: AuditStatus[] = ["queued", "complete", "failed"];

const STATUS_LABELS: Record<AuditStatus, string> = {
  queued: "Queued",
  collecting_dataforseo: "Collecting (DataForSEO)",
  collecting_pagespeed: "Collecting (PageSpeed)",
  collecting_moz: "Collecting (Moz)",
  analyzing: "Analyzing",
  complete: "Complete",
  failed: "Failed",
};

const STATUS_VARIANT: Record<
  AuditStatus,
  "default" | "secondary" | "outline" | "destructive"
> = {
  queued: "secondary",
  collecting_dataforseo: "secondary",
  collecting_pagespeed: "secondary",
  collecting_moz: "secondary",
  analyzing: "secondary",
  complete: "default",
  failed: "destructive",
};

export default function SiteAuditsPage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const canCreate = userHasPermission(user?.effectivePermissions, "site_audits.create");
  const isAdmin = user?.userType === "admin";
  const [open, setOpen] = useState(false);
  const [domain, setDomain] = useState("");
  const [competitors, setCompetitors] = useState("");
  const [brief, setBrief] = useState("");

  const { data, isLoading } = useQuery<{ jobs: SiteAuditJob[]; total: number }>({
    queryKey: ["/api/site-audit"],
    queryFn: getQueryFn({ on401: "throw" }),
    refetchInterval: 10_000,
  });

  const { data: feedStatus } = useQuery<FeedStatusView>({
    queryKey: ["/api/site-audit/feed-status"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const createMutation = useMutation({
    mutationFn: (body: {
      domain: string;
      competitors?: string[];
      brief?: string;
    }) => apiRequest("POST", "/api/site-audit", body),
    onSuccess: async () => {
      await invalidateSiteAuditQueries();
      toast({ title: "Audit queued", description: `Audit for ${domain} is running.` });
      setOpen(false);
      setDomain("");
      setCompetitors("");
      setBrief("");
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to start audit.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (row: { id: string; force?: boolean }) =>
      apiRequest("DELETE", `/api/site-audit/${row.id}${row.force ? "?force=true" : ""}`),
    onSuccess: () => {
      invalidateSiteAuditQueries();
      toast({ title: "Deleted" });
    },
  });

  const rerunMutation = useMutation({
    mutationFn: async (row: SiteAuditJob) => {
      const response = await apiRequest("POST", `/api/site-audit/${row.id}/rerun`);
      return (await response.json()) as { job: SiteAuditJob };
    },
    onSuccess: async (body, row) => {
      await invalidateSiteAuditQueries();
      toast({
        title: "Audit re-run queued",
        description: `A new run for ${row.domain} has started.`,
      });
      navigate(`/site-audits/${body.job.id}`);
    },
    onError: (error) => {
      toast({
        title: "Could not re-run audit",
        description: apiErrorMessage(error, "Failed to re-run audit."),
        variant: "destructive",
      });
    },
  });

  const columns: ColumnDef<SiteAuditJob>[] = [
    {
      key: "domain",
      header: "Domain",
      cell: (row) => (
        <button
          className="font-medium text-primary hover:underline text-left"
          onClick={() => navigate(`/site-audits/${row.id}`)}
        >
          {row.domain}
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            {row.runCount} {row.runCount === 1 ? "run" : "runs"}
          </span>
        </button>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (row) => (
        <Badge variant={STATUS_VARIANT[row.status]}>
          {STATUS_LABELS[row.status]}
        </Badge>
      ),
    },
    {
      key: "createdAt",
      header: "Created",
      cell: (row) => new Date(row.createdAt).toLocaleString(),
    },
    {
      key: "actions",
      header: "",
      cell: (row) => (
        <div className="flex gap-2 justify-end">
          {canCreate && (
            <Button
              variant="ghost"
              size="icon"
              title="Re-run audit"
              aria-label={`Re-run audit for ${row.domain}`}
              disabled={isSiteAuditInProgress(row.status) || rerunMutation.isPending}
              onClick={() => {
                if (
                  confirm(
                    `Re-run the audit for ${row.domain}? This starts a new run with the same settings and keeps this one as history. It usually takes 3–5 minutes.`,
                  )
                ) {
                  rerunMutation.mutate(row);
                }
              }}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/site-audits/${row.id}`)}
          >
            <ExternalLink className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Delete audit for ${row.domain}`}
            disabled={
              !DELETABLE.includes(row.status) &&
              !(isAdmin && isSiteAuditInProgress(row.status))
            }
            onClick={() => {
              const running = isSiteAuditInProgress(row.status) && row.status !== "queued";
              const message = running
                ? `This audit for ${row.domain} is still running. Delete it anyway?`
                : `Delete audit for ${row.domain}?`;
              if (confirm(message)) {
                deleteMutation.mutate({ id: row.id, force: running });
              }
            }}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  function handleCreate() {
    const trimmed = domain.trim();
    if (!trimmed) return;
    const competitorList = competitors
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean)
      .slice(0, 3);

    createMutation.mutate({
      domain: trimmed,
      competitors: competitorList.length ? competitorList : undefined,
      brief: brief.trim() || undefined,
    });
  }

  return (
    <div>
      <PageHeader
        title="Site Audits"
        description="Run SEO audits on prospect domains to generate pitch-ready reports."
        action={
          <Button onClick={() => setOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Audit
          </Button>
        }
      />

      <div className="space-y-4 p-6">
        {feedStatus && (
          <p className="text-sm text-muted-foreground">{formatFeedStatusLine(feedStatus)}</p>
        )}
        <DataTable
          columns={columns}
          data={data?.jobs ?? []}
          isLoading={isLoading}
          rowKey={(row) => row.id}
        />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New Site Audit</DialogTitle>
            <DialogDescription>
              Enter a domain to audit. The audit runs in the background and takes 3–5 minutes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="domain">Domain *</Label>
              <Input
                id="domain"
                placeholder="example.com"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="competitors">Competitors (comma-separated, max 3)</Label>
              <Input
                id="competitors"
                placeholder="competitor1.com, competitor2.com"
                value={competitors}
                onChange={(e) => setCompetitors(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="brief">Brief / Directions (optional)</Label>
              <Textarea
                id="brief"
                placeholder="Focus on ecommerce pages. Prospect is concerned about losing traffic since Q3."
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!domain.trim() || createMutation.isPending}
              >
                {createMutation.isPending ? "Starting…" : "Run Audit"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
